import type { InputHTMLAttributes } from 'react';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  /** Error copy; also wires aria-invalid + aria-describedby. */
  error?: string | null;
  /**
   * Guidance that belongs to this field. Wired through `aria-describedby` so
   * it is announced on focus rather than floating unattached beneath the input.
   */
  hint?: string;
}

export function TextField({ id, label, error, hint, ...rest }: TextFieldProps) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  // Error supersedes hint, so only one message shows and only that one is
  // described-by.
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-buds-dark">
        {label}
      </label>
      <input
        {...rest}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={[
          'mt-2 h-12 w-full min-w-0 max-w-full rounded-xl border bg-buds-surface px-4 text-base text-buds-dark',
          // Keep the native date picker, but size its field like other inputs
          // and center WebKit's date text within the fixed control height.
          rest.type === 'date' ? 'flex items-center appearance-none' : 'block',
          'placeholder:text-buds-muted',
          'focus-ring',
          error ? 'border-buds-danger' : 'border-buds-muted/80',
        ].join(' ')}
      />
      {error ? (
        <p
          id={errorId}
          role="alert"
          className="mt-2 text-sm leading-relaxed text-buds-danger"
        >
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-2 text-sm leading-relaxed text-buds-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
